/**
 * registry.ts — per-discipline DISPLAY policy.
 *
 * The Engine has a discipline policy too, and it decides real things: which
 * router runs, which trace domains are legal, where a diagram file lives. This
 * table decides none of that. It decides what a family is called in a heading
 * and what colour a tab is.
 *
 * Keeping the two apart is the point. If this table is wrong the workbench
 * looks odd; if it were allowed to decide a trace domain, the workbench would
 * be a second engineering authority, which §3.2 exists to prevent.
 */

export interface DisciplineDisplay {
  label: string;
  /** One line, for an empty state or a tooltip. */
  description: string;
  accent: string;
}

const DISPLAY: Record<string, DisciplineDisplay> = {
  pid:        { label: "P&ID",              description: "Piping and instrumentation", accent: "#2f7d95" },
  electrical: { label: "Electrical",        description: "Single-line and distribution", accent: "#a4712a" },
  loop:       { label: "Loop",              description: "Instrument loop diagrams", accent: "#6b5ba8" },
  civil:      { label: "Civil",             description: "Civil and structural", accent: "#5d7a4a" },
  flowsheet:  { label: "Flowsheet",         description: "Process flow", accent: "#2f6f8f" },
  control:    { label: "Control",           description: "Control system architecture", accent: "#7a4f8f" },
  logic:      { label: "Logic",             description: "Binary and interlock logic", accent: "#8f4f6b" },
  safety:     { label: "Safety",            description: "Safety instrumented functions", accent: "#a33a3a" },
  layout:     { label: "Layout",            description: "Plot plans and arrangements", accent: "#57606a" },
  block:      { label: "Block",             description: "Block and overview diagrams", accent: "#4a6b7a" },
};

const FALLBACK: DisciplineDisplay = { label: "Diagram", description: "Diagram", accent: "#57606a" };

export function disciplineDisplay(diagramType: string): DisciplineDisplay {
  // An unknown family is displayed neutrally rather than hidden: a diagram this
  // client has no label for is still a diagram the project contains.
  return DISPLAY[diagramType] ?? { ...FALLBACK, label: diagramType };
}

export function knownDiagramTypes(): string[] {
  return Object.keys(DISPLAY);
}
